import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState, useEffect } from "react";
import { getPages, deletePage } from "@/service/weexpertService";
import { Link, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { Trash2 } from "lucide-react";

export function PagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPages = () => {
    setLoading(true);
    getPages()
      .then((res) => {
        setPages(res.data);
      })
      .catch(() => {
        toast({ title: "Error", description: "Failed to load pages", variant: "destructive" });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handleDelete = (id) => {
    deletePage(id)
      .then(() => {
        toast({ title: "Page deleted" });
        fetchPages();
      })
      .catch(() => {
        toast({ title: "Error", description: "Failed to delete page", variant: "destructive" });
      });
  };

  const columns = [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <Link className="text-blue-600 hover:underline" to={`/pages/${row.original.id}`}>
          {row.original.title}
        </Link>
      ),
      key: uuidv4(),
    },
    {
      accessorKey: "slug",
      header: "Slug",
      cell: ({ row }) => row.original.slug || "—",
      key: uuidv4(),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === "PUBLISHED" ? "default" : "secondary"}>
            {status}
          </Badge>
        );
      },
      key: uuidv4(),
    },
    {
      accessorKey: "updatedAt",
      header: "Updated At",
      cell: ({ row }) => {
        const date = row.original.updatedAt;
        return date ? format(new Date(date), "yyyy-MM-dd HH:mm") : "—";
      },
      key: uuidv4(),
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => {
        if (row.original.status === "DRAFT") {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                handleDelete(row.original.id);
              }}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          );
        }
        return null;
      },
      key: uuidv4(),
    },
  ];

  const table = useReactTable({
    data: pages,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="relative">
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="flex justify-between py-8 pr-8 items-center bg-grayLightMainBg">
          <div className="ml-8 text-4xl">Pages</div>
          <Button onClick={() => navigate("/pages/create")}>
            Create Page
          </Button>
        </div>
      </div>
      <div className="w-full px-8 py-4 bg-grayLightMainBg">
        <div className="rounded-md border">
          {!loading && (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={uuidv4()}>
                    {headerGroup.headers.map((header) => (
                      <TableHead className="pt-3 pb-3" key={uuidv4()}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell className="relative text-sm pt-3 pb-3" key={uuidv4()}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No pages yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
